/**
 * Node ID conventions and shared constants.
 */

// Node ID helpers
export function nodeIdFromItem(item) {
  if (item.type === 'actor' || item.media_type === 'person') return 'a' + item.id;
  if (item.type === 'movie' || item.media_type === 'movie') return 'm' + item.id;
  if (item.type === 'tv' || item.media_type === 'tv') return 't' + item.id;
  return '';
}

export function parseNodeId(nodeId) {
  if (!nodeId || nodeId.length < 2) return { prefix: '', numId: 0 };
  const prefix = nodeId[0];
  const numId = parseInt(nodeId.slice(1), 10);
  return { prefix, numId };
}

export function isActorNode(id) { return id?.startsWith('a'); }
export function isMovieNode(id) { return id?.startsWith('m'); }
export function isTvNode(id) { return id?.startsWith('t'); }
export function isMediaNode(id) { return isMovieNode(id) || isTvNode(id); }

// Default ban list - degenerate path prevention
export const DEFAULT_BAN_LIST = {
  actors: [
    // Stan Lee
    16828,
  ],
  titles: [
    // The Oscars
    { type: 'tv', id: 27023 },
    // The Simpsons
    { type: 'tv', id: 456 },
  ],
};

// Cytoscape style config
export const CY_STYLE = [
  {
    selector: 'node', style: {
      'label': 'data(label)', 'background-image': 'data(image)', 'shape': 'data(shape)',
      'width': 80, 'height': 120, 'background-fit': 'cover', 'background-color': 'data(backgroundColor)',
      'background-image-opacity': 'data(opacity)', 'border-color': 'data(borderColor)',
      'border-width': 'data(borderWidth)', 'border-opacity': 1,
      'overlay-color': 'rgba(220,182,62,0.47)',
      'text-halign': 'center', 'text-valign': 'bottom', 'color': '#ffffff',
      'font-family': "'AkkuratMono-Regular','IBM Plex Mono',monospace",
      'font-size': '7px', 'text-transform': 'uppercase', 'text-max-width': 80,
      'text-margin-y': -8, 'text-wrap': 'wrap',
      'text-background-color': '#000000', 'text-background-opacity': 0.5, 'text-background-padding': '2px',
    },
  },
  { selector: 'edge', style: { 'line-color': 'data(lineColor)', 'width': 'data(width)' } },
  { selector: ':selected', style: { 'border-color': '#BCA356', 'background-color': '#BCA356', 'border-width': 3, 'border-style': 'solid' } },
];

export function cyLayout(el) {
  const d3ok = typeof window !== 'undefined' && typeof window.cytoscapeD3Force !== 'undefined';
  if (!d3ok) return { name: 'cose', animate: true, fit: true, nodeRepulsion: 8000, padding: 20 };
  const vv = window.visualViewport;
  const w = el ? Math.min(el.clientWidth, vv ? vv.width : window.innerWidth) : (vv ? vv.width : window.innerWidth);
  const h = el ? Math.min(el.clientHeight, vv ? vv.height : window.innerHeight) : (vv ? vv.height : window.innerHeight);
  return {
    name: 'd3-force', animate: true, fit: true, infinite: true,
    linkId: e => e.id, linkDistance: 120, linkStrength: () => 0.3,
    xX: 0.5, xStrength: w > h ? 0 : (h / w) / 10, yY: 0.5, yStrength: h > w ? 0 : (w / h) / 10,
    alphaMin: 0, alphaTarget: 0.05,
    velocityDecay: 0.6, collideRadius: 5, collideStrength: 1, manyBodyStrength: -1500,
  };
}

// Game mode identifiers
export const MODES = {
  CUSTOM: 'custom',
  DAILY: 'daily',
  TIME_ATTACK: 'time-attack',
  CHAIN_REACTION: 'chain-reaction',
  EXPLORATION: 'exploration',
  SUCCESSIVE_ROUNDS: 'successive-rounds',
  TUG_OF_WAR: 'tug-of-war',
};

// Scoring directions
export const SCORING = {
  STANDARD: 'standard',     // lowest uniqueMediaCount wins
  CHAIN: 'chain-reaction',  // highest uniqueMediaCount wins
};

// Default game options
export const DEFAULT_GAME_OPTIONS = {
  mode: MODES.CUSTOM,
  scoring: SCORING.STANDARD,
  crewConnections: false,
  fogOfWar: false,
  ghostMode: false,
  hintMode: 'unlimited',    // 'unlimited' | 'finite'
  hintPool: 5,
  timeLimit: 0,             // 0 = no time limit, otherwise seconds
  targetCount: 2,
  pathConstraints: {
    genre: null,
    decade: null,
    budgetCap: null,
    rotating: false,
    rotateInterval: 5,      // guesses between rotation
  },
  banList: { ...DEFAULT_BAN_LIST },
  connectionPreview: true,
  toastNotifications: true,
};
